import { CloseOutlined } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Stack,
} from "@mui/material";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

const AlertContext = createContext({ open: false });

const useActionDialog = () => {
    const ctx = useContext(AlertContext) || {};
    return (
        (ctx && ctx.showDialog) ||
        (() => console.warn("Function showAlert is used outside of ActionDialogContainer"))
    );
};

export default useActionDialog;

//actions, title, message

export const ActionDialogContainer = ({ children }) => {
    const [state, setState] = useState({ open: false });

    const showDialog = (title, message, actions, options) => {
        setState({ title, message, actions, options, open: true });
    };

    const hideAlert = () => {
        setState({ open: false });
    };

    return (
        <AlertContext.Provider value={{ showDialog }}>
            {children}
            <DialogComponent {...state} handleClose={hideAlert} />
        </AlertContext.Provider>
    );
};

const DialogComponent = ({ open, handleClose, actions, title, message, options }) => {
    const [loading, setLoading] = useState({});
    useEffect(() => {
        if (open) {
            setLoading({});
        }
    }, [open]);

    const disabled = Object.keys(loading).length > 0;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            maxWidth="sm"
            fullWidth>
            <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
                disabled={disabled}>
                <CloseOutlined />
            </IconButton>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">{message}</DialogContentText>
                {options?.helperText && (
                    <DialogContentText sx={{ mt: 2 }}>{options.helperText}</DialogContentText>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2, px: 3, justifyContent: "space-between" }}>
                <Stack direction="row" spacing={1}>
                    {(actions || [])
                        .filter((a) => !a.primary)
                        .map((action, key) => (
                            <LoadingButton
                                key={key}
                                onClick={async () => {
                                    try {
                                        setLoading({ [`secondary-${key}`]: true });
                                        await action.onClick?.();
                                    } finally {
                                        setLoading({ [`secondary-${key}`]: false });
                                    }
                                    handleClose?.();
                                }}
                                variant={action.variant || "text"}
                                autoFocus={action.autoFocus}
                                disabled={disabled}
                                loading={loading[`secondary-${key}`]}>
                                {action.title}
                            </LoadingButton>
                        ))}
                </Stack>
                <Stack direction="row" spacing={1}>
                    {(actions || [])
                        .filter((a) => a.primary)
                        .map((action, key) => (
                            <LoadingButton
                                key={key}
                                onClick={async () => {
                                    try {
                                        setLoading({ [`primary-${key}`]: true });
                                        await action.onClick?.();
                                    } finally {
                                        setLoading({ [`primary-${key}`]: false });
                                    }
                                    handleClose?.();
                                }}
                                variant={action.variant || "outlined"}
                                autoFocus={action.autoFocus}
                                disabled={disabled}
                                loading={loading[`primary-${key}`]}>
                                {action.title}
                            </LoadingButton>
                        ))}
                </Stack>
            </DialogActions>
        </Dialog>
    );
};
